import { useNavigate } from 'react-router-dom';
import {
  X,
  Clock,
  Coffee,
  Play,
  Users,
  DollarSign,
  TrendingUp,
  LogOut,
  Calendar,
} from 'lucide-react';
import clsx from 'clsx';
import { useShiftStore } from '../stores/shiftStore';
import { getShiftTypeConfig } from '../config/shiftTypeConfig';

interface TimeClockModalProps {
  isOpen: boolean;
  onClose: () => void;
  liveStats: {
    tablesServed: number;
    guestsServed: number;
    salesTotalCents: number;
    tipsTotalCents: number;
  };
  clockInTime: Date | null;
  displayDuration: string;
  displayBreakDuration: string;
  totalBreakMinutes: number;
  onBreakToggle: () => void;
  isBreakLoading: boolean;
}

export default function TimeClockModal({
  isOpen,
  onClose,
  liveStats,
  clockInTime,
  displayDuration,
  displayBreakDuration,
  totalBreakMinutes,
  onBreakToggle,
  isBreakLoading,
}: TimeClockModalProps) {
  const navigate = useNavigate();
  const { shiftType, isOnBreak, endShift } = useShiftStore();

  if (!isOpen) return null;

  const shiftConfig = getShiftTypeConfig(shiftType);
  const ShiftIcon = shiftConfig.icon;

  const formatTime = (date: Date | null): string => {
    if (!date) return '--:--';
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatBreakMinutes = (minutes: number): string => {
    if (minutes < 1) return '0 min';
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleEndShift = () => {
    endShift();
    onClose();
    navigate('/post-shift');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div
          className={clsx(
            'rounded-t-2xl px-6 py-4 flex items-center justify-between',
            isOnBreak ? 'bg-slate-600' : 'bg-primary-700'
          )}
        >
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">Time Clock</h2>
              <p className="text-sm opacity-80">
                {isOnBreak ? 'On Break' : 'Active Shift'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Shift Type & Duration */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={clsx(
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  shiftConfig.bgColor
                )}
              >
                <ShiftIcon className={clsx('w-6 h-6', shiftConfig.color)} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Shift Type</p>
                <p className="font-semibold text-slate-900">{shiftConfig.label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Working Time</p>
              <p className="font-semibold text-2xl text-slate-900 tabular-nums">
                {displayDuration}
              </p>
            </div>
          </div>

          {/* Shift Times */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Clock In</span>
              </div>
              <span className="font-medium text-slate-900 tabular-nums">
                {formatTime(clockInTime)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-600">
                <Coffee className="w-4 h-4" />
                <span className="text-sm">Total Break Time</span>
              </div>
              <span className="font-medium text-slate-900">
                {formatBreakMinutes(totalBreakMinutes)}
              </span>
            </div>
          </div>

          {/* Break Status */}
          {isOnBreak && (
            <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Coffee className="w-5 h-5 text-slate-600 animate-pulse" />
                  <span className="font-medium text-slate-800">Currently on Break</span>
                </div>
                <span className="text-2xl font-bold text-slate-700 tabular-nums">
                  {displayBreakDuration}
                </span>
              </div>
            </div>
          )}

          {/* Shift Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs">Tables Served</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {liveStats.tablesServed}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs">Guests Served</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {liveStats.guestsServed}
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1.5 text-slate-500 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs">Total Sales</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(liveStats.salesTotalCents)}
              </p>
            </div>
            <div className="bg-primary-50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1.5 text-primary-600 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs">Tips</span>
              </div>
              <p className="text-2xl font-bold text-primary-700">
                {formatCurrency(liveStats.tipsTotalCents)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onBreakToggle}
              disabled={isBreakLoading}
              className={clsx(
                'flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all',
                isOnBreak
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-slate-600 text-white hover:bg-slate-700',
                isBreakLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isOnBreak ? (
                <>
                  <Play className="w-4 h-4" />
                  <span>End Break</span>
                </>
              ) : (
                <>
                  <Coffee className="w-4 h-4" />
                  <span>Start Break</span>
                </>
              )}
            </button>
            <button
              onClick={handleEndShift}
              disabled={isOnBreak}
              className={clsx(
                'flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2',
                'bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors',
                isOnBreak && 'opacity-50 cursor-not-allowed'
              )}
            >
              <LogOut className="w-4 h-4" />
              <span>End Shift</span>
            </button>
          </div>

          {isOnBreak && (
            <p className="text-center text-xs text-slate-500">
              End your break before clocking out
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
